import { trackGptPageView } from "./gptTracking";

const loaded = {
  gtag: false,
  adroll: false,
  elfsight: false,
  fbBaseCode: false,
};

const initializedFbPixels = new Set();
const ADROLL_ADV_ID = "MZI43QDBPRDSDNF4O2KUIN";
const FBEVENTS_SRC = "https://connect.facebook.net/en_US/fbevents.js";
const FB_NOSCRIPT_ID = "ss-facebook-pixel-noscript";

const setConsentDebug = (key, value) => {
  if (typeof window === "undefined") return;

  window.__ssConsentDebug = {
    ...(window.__ssConsentDebug || {}),
    [key]: value,
  };
};

const injectScript = (src, { id, async = true } = {}) => {
  if (typeof document === "undefined") return null;
  if (id && document.getElementById(id)) {
    return document.getElementById(id);
  }

  const script = document.createElement("script");
  if (id) script.id = id;
  script.src = src;
  script.async = async;
  document.head.appendChild(script);
  return script;
};

/**
 * Official Meta Pixel base code from Facebook docs.
 * https://developers.facebook.com/docs/meta-pixel/get-started
 * Call BEFORE AdRoll so our pixel owns fbq first.
 */
const installFacebookBaseCode = () => {
  if (loaded.fbBaseCode) return;
  loaded.fbBaseCode = true;

  // Exact Facebook Pixel bootstrap (docs), written as a call so ESLint is happy.
  (function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", FBEVENTS_SRC);

  setConsentDebug("fbEventsScript", "base-code-installed");
};

const injectFacebookNoscript = (pixelId) => {
  if (document.getElementById(FB_NOSCRIPT_ID)) return;

  const noscript = document.createElement("noscript");
  noscript.id = FB_NOSCRIPT_ID;
  const img = document.createElement("img");
  img.height = 1;
  img.width = 1;
  img.style.display = "none";
  img.src = `https://www.facebook.com/tr?id=${encodeURIComponent(
    pixelId
  )}&ev=PageView&noscript=1`;
  noscript.appendChild(img);
  document.body.appendChild(noscript);
};

export const loadGoogleAnalytics = (measurementId) => {
  if (!measurementId || loaded.gtag) return;
  if (typeof window === "undefined") return;

  loaded.gtag = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", measurementId, {
    anonymize_ip: true,
    send_page_view: true,
  });

  // Late consent: capture gpt_page_view if user is already on /gpt.
  trackGptPageView(
    typeof window !== "undefined" ? window.location.pathname : ""
  );

  injectScript(`https://www.googletagmanager.com/gtag/js?id=${measurementId}`, {
    id: "ss-ga-gtag",
  });
};

/**
 * Consent-gated Meta Pixel using the official Facebook docs snippet.
 */
export const loadFacebookPixel = (pixelId) => {
  const id = String(pixelId || "").trim();
  if (!id || typeof window === "undefined" || typeof document === "undefined") {
    return;
  }

  installFacebookBaseCode();

  if (typeof window.fbq !== "function") {
    setConsentDebug("facebookPixel", { error: "fbq missing after base code" });
    return;
  }

  if (!initializedFbPixels.has(id)) {
    window.fbq("init", id);
    initializedFbPixels.add(id);
    window.__ssFbPixelId = id;
    injectFacebookNoscript(id);
  }

  window.fbq("track", "PageView");
  setConsentDebug("facebookPixel", {
    pixelId: id,
    initialized: Array.from(initializedFbPixels),
    queuedEvents: window.fbq?.queue?.length || 0,
  });
};

export const loadAdRoll = () => {
  if (loaded.adroll) return;
  if (typeof window === "undefined") return;

  loaded.adroll = true;
  window.adroll_adv_id = ADROLL_ADV_ID;
  window.adroll_pix_id = "DL2HCWXKTRBQFB6ZHV6DVM";
  window.adroll_version = "2.0";
  window.__adroll_loaded = true;
  window.adroll = window.adroll || [];
  window.adroll.f = [
    "setProperties",
    "identify",
    "track",
    "identify_email",
    "get_cookie",
  ];

  for (let i = 0; i < window.adroll.f.length; i += 1) {
    const name = window.adroll.f[i];
    window.adroll[name] =
      window.adroll[name] ||
      ((n) =>
        function adrollProxy() {
          window.adroll.push([n, arguments]);
        })(name);
  }

  injectScript(`https://s.adroll.com/j/${ADROLL_ADV_ID}/roundtrip.js`, {
    id: "ss-adroll",
  });

  if (typeof window.adroll.track === "function") {
    window.adroll.track("pageView");
  }
};

export const loadElfsightPlatform = () => {
  if (loaded.elfsight) return;
  if (typeof window === "undefined") return;

  loaded.elfsight = true;
  injectScript("https://elfsightcdn.com/platform.js", {
    id: "ss-elfsight-platform",
  });
};

export const trackRouteChange = (pathname = "", search = "") => {
  if (typeof window === "undefined") return;
  const pagePath = `${pathname}${search}`;

  if (typeof window.gtag === "function") {
    window.gtag("event", "page_view", { page_path: pagePath });
  }
  trackGptPageView(pathname);
  if (typeof window.fbq === "function" && initializedFbPixels.size > 0) {
    window.fbq("track", "PageView");
  }
};
